/**
 * Mirrors the backend admin coupon response DTOs exactly
 * (`InviteCloudBackend/src/modules/pricing/coupon/presentation/response/`).
 * Money is integer paise; PERCENTAGE `discountValue` is basis points (1% = 100).
 */

export type CouponStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'EXHAUSTED';
export type DiscountType = 'FLAT' | 'PERCENTAGE';

export const COUPON_STATUSES: CouponStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'EXPIRED', 'EXHAUSTED'];

/** List projection — the backend omits `collectionCodes`/`usageLimitPerUser` here (N+1 avoidance). */
export interface CouponSummary {
  id: string;
  code: string;
  label: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountPaise: number | null;
  startAt: string;
  endAt: string;
  usageLimitGlobal: number | null;
  usedCount: number;
  status: CouponStatus;
  createdAt: string;
}

export interface CouponDetail extends CouponSummary {
  usageLimitPerUser: number | null;
  /** Empty = applies to every Collection. */
  collectionCodes: string[];
  createdByAdminId: string;
  version: number;
  updatedAt: string;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CouponUsage {
  purchaseId: string;
  userId: string;
  discountPaise: number;
  createdAt: string;
}

export interface CouponListFilters {
  status?: CouponStatus;
  search?: string;
}

/** Wire payload for create/update. `null` clears a nullable field on update. */
export interface CouponWriteFields {
  label: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountPaise: number | null;
  startAt: string;
  endAt: string;
  usageLimitGlobal: number | null;
  usageLimitPerUser: number | null;
  collectionCodes: string[];
}

export type CreateCouponInput = { code: string } & CouponWriteFields;

/** Diff-only: the backend freezes pricing fields by *presence* once a coupon is used. */
export type UpdateCouponInput = { version: number } & Partial<CouponWriteFields>;

export interface CollectionOption {
  code: string;
  displayName: string;
  isActive: boolean;
}
